/**
 * web/src/hooks/useAuth.ts
 * Convenience aliases. Pages call useAuth() to get the user; components
 * needing only role can use useRole().
 */
import { useMe } from './queries/useMe';
import type { Role } from '../api/types';

/**
 * Page-level auth hook. Returns `{ me, isPending, error }` so callers can
 * render skeletons while loading and route on error if they need to.
 *
 * @returns { me, isPending, error }
 * @sideEffects same as useMe — HTTP GET on first mount of the session
 * @idempotent yes — multiple calls share the React Query cache entry
 */
export function useAuth() {
  const { data: me, isPending, error } = useMe();
  return { me, isPending, error };
}

/**
 * Returns the authenticated user's role (or undefined while loading / on error).
 * Components that role-filter UI affordances should branch on this; the server
 * remains the single authorization authority (T-05-02-04).
 */
export function useRole(): Role | undefined {
  return useMe().data?.role;
}
